export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"] as const;
export type ImageMediaType = (typeof ACCEPTED_IMAGE_TYPES)[number];

export const MAX_IMAGES_PER_QUESTION = 3;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export interface AttachmentCandidate {
  type: string;
  size: number;
}

export interface QuestionImage {
  mediaType: ImageMediaType;
  data: string;
}

const DATA_URL = /^data:([\w/+.-]+);base64,(.+)$/;

const isAcceptedType = (type: string): type is ImageMediaType =>
  (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);

/** Why a file can't be attached to the current question, or undefined when it can. */
export function attachmentProblem(
  candidate: AttachmentCandidate,
  alreadyAttached: number,
): string | undefined {
  if (alreadyAttached >= MAX_IMAGES_PER_QUESTION) {
    return `At most ${MAX_IMAGES_PER_QUESTION} screenshots per question`;
  }
  if (!isAcceptedType(candidate.type)) return "Only PNG, JPEG, GIF or WebP screenshots";
  if (candidate.size > MAX_IMAGE_BYTES) return "Each screenshot must be 10 MB or smaller";
  return undefined;
}

/** Splits a base64 data URL (from FileReader) into the API's { mediaType, data } shape. */
export function dataUrlToImage(dataUrl: string): QuestionImage | undefined {
  const match = DATA_URL.exec(dataUrl);
  if (match === null) return undefined;
  const [, mediaType, data] = match;
  return isAcceptedType(mediaType) ? { mediaType, data } : undefined;
}
